import * as React from "react";
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid2,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import styled from "styled-components";
import type {
    Index,
    IndexCurve,
    IndexCurveExponential,
    IndexCurveLinear,
    IndexSubCategoryConfig,
} from "src/scenes/indexes-settings/indexes-types";
import { IndexCurveTypes, IndexImpactTypes } from "src/scenes/indexes-settings/constants";
import Switch from "@mui/material/Switch";
import ExploreIcon from "@mui/icons-material/Explore";
import FittingChart from "src/scenes/indexes-settings/fitting-chart";
import { InView } from "react-intersection-observer";

const CustomPaper = styled(Paper)`
    border-radius: 14px;
    width: 100%;
    transition: opacity 0.4s ease-out;
    position: static;
`;

const PaperTitle = styled.h3`
    margin: 0;
    padding: 7px 0 6px 18px;
    font-size: 14px;
    background-color: #5b5b5b;
    border-radius: 14px 14px 0 0;
    backface-visibility: hidden;
    transform: translateZ(0) scale(1, 1);
    text-transform: capitalize;
`;

const Fraction = styled.div`
    display: inline-block;
    vertical-align: middle;
    margin: 0 0.2em 0.4ex;
    text-align: center;
    outline: 2px solid green;
    border-radius: 8px;
    & > span {
        display: block;
        padding-top: 0.15em;
    }
    & span {
        &.fdn {
            border-top: 1px solid white;
        }
        &.bar {
            display: none;
        }
    }
`;

const SingleIndexSettings = ({
    index,
    subcategory,
    editIndex,
    showAdvanced,
}: {
    index: Index;
    subcategory: IndexSubCategoryConfig;
    editIndex: (indexConfig: Index) => void;
    showAdvanced: boolean;
}) => {
    const [impact, setImpact] = React.useState<IndexImpactTypes>(index.impact);
    const [curve, setCurve] = React.useState<IndexCurve>(index.curve);
    const [normalization, setNormalization] = React.useState<Index["normalization"]>(
        index.normalization
    );
    const [weight, setWeight] = React.useState<Index["weight"]>(index.weight);
    const [inactive, setInactive] = React.useState<Index["inactive"]>(index.inactive);
    const [enabled, setEnabled] = React.useState<Index["enabled"]>(index.enabled);

    React.useEffect(() => {
        if (index) {
            setImpact(index.impact);
            setCurve(index.curve);
            setNormalization(index.normalization);
            setWeight(index.weight);
            setEnabled(index.enabled);
            setInactive(index.inactive);
        }
    }, [index && JSON.stringify(index)]);

    React.useEffect(() => {
        editIndex({ ...index, curve, impact, inactive, normalization, weight, enabled });
    }, [
        impact,
        curve && JSON.stringify(curve),
        normalization && JSON.stringify(normalization),
        weight && JSON.stringify(weight),
        enabled,
        inactive,
    ]);

    const renderEquation = () => {
        let equation;
        if (!showAdvanced) return null;
        if (impact === "positive") {
            equation = (
                <Fraction>
                    <span className="fup">
                        greatest(least(raw_value, {normalization.max}) - {normalization.min}, 0)
                    </span>
                    <span className="bar">/</span>
                    <span className="fdn">
                        {normalization.max} - {normalization.min}
                    </span>
                </Fraction>
            );
        } else {
            equation = (
                <Fraction>
                    <span className="fup">
                        {normalization.max} - greatest(least(raw_value, {normalization.max}),
                        {normalization.min})
                    </span>
                    <span className="bar">/</span>
                    <span className="fdn">
                        {normalization.max} - {normalization.min}
                    </span>
                </Fraction>
            );
        }

        if (curve.type === "linear") {
            equation = (
                <span style={{ border: "2px solid blue", borderRadius: "8px" }}>
                    {equation} * {curve.coefficient} + {curve.constant}
                </span>
            );
        }

        if (curve.type === "exponential") {
            equation = (
                <span style={{ outline: "2px solid blue", padding: "15px 0", borderRadius: "8px" }}>
                    <var>
                        <span style={{ fontSize: "24px" }}>(</span>
                        {equation}
                        <span style={{ fontSize: "24px" }}>)</span>
                        <sup style={{ verticalAlign: "top" }}>{curve.power}</sup>
                    </var>
                    + {curve.constant}
                </span>
            );
        }

        return (
            <span
                style={{
                    outline: "2px solid #292929",
                    padding: "18px 3px",
                    borderRadius: "8px",
                }}
            >
                {weight.coefficient}*
                <span style={{ fontSize: "28px", verticalAlign: "sub" }}>(</span>
                {equation}
                <span style={{ fontSize: "28px" }}>)</span>
            </span>
        );
    };

    // const handleChange1 = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     setChecked([event.target.checked, event.target.checked]);
    // };

    return (
        <>
            <FormControlLabel
                label={<span style={{ fontSize: "12px" }}>{index.descriptive_name} Enabled</span>}
                control={
                    <Checkbox
                        checked={enabled}
                        size="small"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setEnabled(event.target.checked);
                        }}
                    />
                }
            />
            {enabled && (
                <InView>
                    {({ inView, ref }) => (
                        <CustomPaper ref={ref}>
                            <PaperTitle>{index.descriptive_name}</PaperTitle>
                            {index.isDefault && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    m={1}
                                    style={{ opacity: "0.5" }}
                                >
                                    <ExploreIcon />
                                    <p style={{ fontSize: "12px" }}>Default setup</p>
                                </Stack>
                            )}
                            {inView && (
                                <>
                                    <Grid2 container direction="row" spacing={2} p={2}>
                                        <Grid2
                                            container
                                            direction="column"
                                            size={6}
                                            spacing={2}
                                            p={2}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography sx={{ fontSize: 11 }}>
                                                    Negative Impact
                                                </Typography>
                                                <Switch
                                                    checked={impact === IndexImpactTypes.positive}
                                                    onChange={() => {
                                                        if (impact === IndexImpactTypes.positive) {
                                                            setImpact(IndexImpactTypes.negative);
                                                        } else {
                                                            setImpact(IndexImpactTypes.positive);
                                                        }
                                                    }}
                                                    size="small"
                                                />
                                                <Typography sx={{ fontSize: 11 }}>
                                                    Positive Impact
                                                </Typography>
                                            </Stack>
                                            {showAdvanced && (
                                                <FormControl size="small">
                                                    <InputLabel
                                                        id="curve-type"
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "0.5rem",
                                                            backgroundColor: "#474747",
                                                        }}
                                                    >
                                                        Type
                                                        <span
                                                            style={{
                                                                width: "12px",
                                                                height: "12px",
                                                                backgroundColor: "blue",
                                                                borderRadius: "50%",
                                                                display: "inline-block",
                                                            }}
                                                        />
                                                    </InputLabel>
                                                    <Select
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        value={curve.type}
                                                        label="Type"
                                                        onChange={(event) => {
                                                            if (
                                                                event.target.value ===
                                                                IndexCurveTypes.linear
                                                            ) {
                                                                setCurve({
                                                                    type: event.target.value,
                                                                    coefficient: 1,
                                                                    constant: 0,
                                                                });
                                                            }
                                                            if (
                                                                event.target.value ===
                                                                IndexCurveTypes.exponential
                                                            ) {
                                                                setCurve({
                                                                    type: event.target.value,
                                                                    power: 0.67,
                                                                    constant: 0,
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="linear">Linear</MenuItem>
                                                        <MenuItem value="exponential">
                                                            Exponential
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            )}
                                            {showAdvanced && curve.type === IndexCurveTypes.linear && (
                                                <Grid2 spacing={2} direction="row" container>
                                                    <Grid2 size={6}>
                                                        <TextField
                                                            id="lin-coeff"
                                                            label="Weight"
                                                            type="number"
                                                            size="small"
                                                            inputProps={{
                                                                step: 0.1,
                                                            }}
                                                            value={curve.coefficient}
                                                            onChange={(
                                                                event: React.ChangeEvent<HTMLInputElement>
                                                            ) => {
                                                                setCurve((prevState) => ({
                                                                    ...(prevState as IndexCurveLinear),
                                                                    coefficient: parseFloat(
                                                                        event.target.value
                                                                    ),
                                                                    base: undefined,
                                                                    power: undefined,
                                                                }));
                                                            }}
                                                        />
                                                    </Grid2>
                                                    <Grid2 size={6}>
                                                        <TextField
                                                            id="lin-constant"
                                                            label="Constant"
                                                            type="number"
                                                            size="small"
                                                            inputProps={{
                                                                step: 0.1,
                                                            }}
                                                            value={curve.constant}
                                                            onChange={(
                                                                event: React.ChangeEvent<HTMLInputElement>
                                                            ) => {
                                                                setCurve((prevState) => ({
                                                                    ...(prevState as IndexCurveLinear),
                                                                    constant: parseFloat(
                                                                        event.target.value
                                                                    ),
                                                                    base: undefined,
                                                                    power: undefined,
                                                                }));
                                                            }}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                            )}
                                            {showAdvanced &&
                                                curve.type === IndexCurveTypes.exponential && (
                                                    <Grid2 spacing={2} direction="row" container>
                                                        <Grid2 size={6}>
                                                            <TextField
                                                                id="exp-power"
                                                                label={
                                                                    <span
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "0.5rem",
                                                                        }}
                                                                    >
                                                                        Power
                                                                        <span
                                                                            style={{
                                                                                width: "12px",
                                                                                height: "12px",
                                                                                backgroundColor:
                                                                                    "green",
                                                                                borderRadius: "50%",
                                                                                display:
                                                                                    "inline-block",
                                                                            }}
                                                                        />
                                                                    </span>
                                                                }
                                                                type="number"
                                                                size="small"
                                                                inputProps={{
                                                                    step: 0.1,
                                                                }}
                                                                value={curve.power}
                                                                onChange={(
                                                                    event: React.ChangeEvent<HTMLInputElement>
                                                                ) => {
                                                                    setCurve((prevState) => ({
                                                                        ...(prevState as IndexCurveExponential),
                                                                        power: parseFloat(
                                                                            event.target.value
                                                                        ),
                                                                        base: undefined,
                                                                    }));
                                                                }}
                                                            />
                                                        </Grid2>
                                                        <Grid2 size={6}>
                                                            <TextField
                                                                id="lin-constant"
                                                                label={
                                                                    <span
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "0.5rem",
                                                                        }}
                                                                    >
                                                                        Constant
                                                                        <span
                                                                            style={{
                                                                                width: "12px",
                                                                                height: "12px",
                                                                                backgroundColor:
                                                                                    "green",
                                                                                borderRadius: "50%",
                                                                                display:
                                                                                    "inline-block",
                                                                            }}
                                                                        />
                                                                    </span>
                                                                }
                                                                type="number"
                                                                size="small"
                                                                inputProps={{
                                                                    step: 0.1,
                                                                }}
                                                                value={curve.constant}
                                                                onChange={(
                                                                    event: React.ChangeEvent<HTMLInputElement>
                                                                ) => {
                                                                    setCurve((prevState) => ({
                                                                        ...(prevState as IndexCurveExponential),
                                                                        constant: parseFloat(
                                                                            event.target.value
                                                                        ),
                                                                        base: undefined,
                                                                    }));
                                                                }}
                                                            />
                                                        </Grid2>
                                                    </Grid2>
                                                )}
                                            <Grid2 spacing={2} direction="row" container>
                                                <Grid2 size={6}>
                                                    <TextField
                                                        id="normalization-min"
                                                        label={
                                                            <span
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "0.5rem",
                                                                }}
                                                            >
                                                                Lower Bound {index.units}
                                                                <span
                                                                    style={{
                                                                        width: "12px",
                                                                        height: "12px",
                                                                        backgroundColor: "green",
                                                                        borderRadius: "50%",
                                                                        display: "inline-block",
                                                                    }}
                                                                />
                                                            </span>
                                                        }
                                                        type="number"
                                                        size="small"
                                                        inputProps={{
                                                            step: 0.1,
                                                        }}
                                                        value={normalization.min}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setNormalization((prevState) => ({
                                                                ...(prevState as Index["normalization"]),
                                                                min: parseFloat(event.target.value),
                                                            }));
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={6}>
                                                    <TextField
                                                        id="normalization-min"
                                                        label={
                                                            <span
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "0.5rem",
                                                                }}
                                                            >
                                                                Upper Bound {index.units}
                                                                <span
                                                                    style={{
                                                                        width: "12px",
                                                                        height: "12px",
                                                                        backgroundColor: "green",
                                                                        borderRadius: "50%",
                                                                        display: "inline-block",
                                                                    }}
                                                                />
                                                            </span>
                                                        }
                                                        type="number"
                                                        size="small"
                                                        inputProps={{
                                                            step: 0.1,
                                                        }}
                                                        value={normalization.max}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setNormalization((prevState) => ({
                                                                ...(prevState as Index["normalization"]),
                                                                max: parseFloat(event.target.value),
                                                            }));
                                                        }}
                                                    />
                                                </Grid2>
                                            </Grid2>
                                            {subcategory.aggregation !== "sum" && (
                                                <Grid2 spacing={2} direction="row" container>
                                                    <Grid2 size={6}>
                                                        <TextField
                                                            id="weight-coefficient"
                                                            label={
                                                                <span
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "0.5rem",
                                                                    }}
                                                                >
                                                                    Weight
                                                                    <span
                                                                        style={{
                                                                            width: "12px",
                                                                            height: "12px",
                                                                            backgroundColor:
                                                                                "#292929",
                                                                            borderRadius: "50%",
                                                                            display: "inline-block",
                                                                        }}
                                                                    />
                                                                </span>
                                                            }
                                                            type="number"
                                                            size="small"
                                                            inputProps={{
                                                                step: 0.1,
                                                            }}
                                                            value={weight.coefficient}
                                                            onChange={(
                                                                event: React.ChangeEvent<HTMLInputElement>
                                                            ) => {
                                                                setWeight((prevState) => ({
                                                                    ...(prevState as Index["weight"]),
                                                                    coefficient: parseFloat(
                                                                        event.target.value
                                                                    ),
                                                                }));
                                                            }}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                            )}
                                            {showAdvanced && (
                                                <FormControl size="small">
                                                    <InputLabel id="inactive type">
                                                        When Inactive Value Should Be
                                                    </InputLabel>
                                                    <Select
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        value={inactive}
                                                        label="When Inactive Value Should Be"
                                                        onChange={(event) => {
                                                            setInactive(
                                                                event.target.value as
                                                                    | "value"
                                                                    | "zero"
                                                                    | "null"
                                                            );
                                                        }}
                                                    >
                                                        <MenuItem value="zero">Zero</MenuItem>
                                                        <MenuItem value="one">One</MenuItem>
                                                        <MenuItem value="null">Null</MenuItem>
                                                        <MenuItem value="value">
                                                            Calculated Value
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid2>
                                        <Grid2 size={5}>
                                            <FittingChart
                                                displayName={index.descriptive_name}
                                                curve={curve}
                                                normalization={normalization}
                                                weight={weight}
                                                impact={impact}
                                            />
                                        </Grid2>
                                    </Grid2>
                                    {renderEquation()}
                                </>
                            )}
                        </CustomPaper>
                    )}
                </InView>
            )}
        </>
    );
};

export default SingleIndexSettings;
